import React, { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useSecurity } from "~/components/Hooks";
import { EDIT_EXPENSE_ERROR } from "~/store/action_types/index.jsx";
import { editExpense, moveExpenseToTrash } from "~/store/actions/actions.expenses.jsx";
import { getAllExpenseCategoriesArray } from "~/store/selectors/selector.expenseCategories.jsx";
import { getExpenseById } from "~/store/selectors/selector.expenses.jsx";
import { getProjectIdByTaskId } from "~/store/selectors/selector.tasks.jsx";
import { ExpenseListItem } from "./ExpenseListItem.js";
import { showErrorToast, showSuccessToast } from "../Toast/index.js";

export const ExpenseListItemContainer = ({ id, ...props }) => {
  const dispatch = useDispatch();
  const [trashConfirmation, setTrashConfirmation] = useState(false);
  const expense = useSelector((state) => getExpenseById(state, id));
  const { t } = useTranslation();

  const expenseCategories = useSelector(getAllExpenseCategoriesArray);

  const availableExpenseCategories = useMemo(() => {
    return expenseCategories.filter((category) => {
      return !category.is_archived || category.id === expense.category_id;
    });
  }, [expense.category_id, expenseCategories]);

  let projectId = useSelector((state) => getProjectIdByTaskId(state, expense.parent_id));
  projectId = expense.parent_type === "Project" ? expense.parent_id : projectId;

  const can = useSecurity();
  const canEdit = can(["edit"], expense);

  const handleMoveToTrash = useCallback(() => {
    setTrashConfirmation(true);
  }, []);

  const closeTrashConfirmation = useCallback(() => {
    setTrashConfirmation(false);
  }, []);

  const moveToTrashCallback = useCallback(() => {
    dispatch(moveExpenseToTrash(expense.id)).then((action) => {
      if (action.error) {
        showErrorToast(t("Failed to move expense to trash"));
      } else {
        showSuccessToast(t("Expense moved to trash"));
      }
    });
    closeTrashConfirmation();
  }, [dispatch, expense.id, closeTrashConfirmation, t]);

  const handleChange = useCallback(
    async (attr) => {
      const { type } = await dispatch(editExpense(projectId, id, attr));

      type === EDIT_EXPENSE_ERROR && showErrorToast(t("Failed to update expense"));
    },
    [dispatch, id, projectId, t]
  );

  return (
    <ExpenseListItem
      {...props}
      expense={expense}
      canEdit={canEdit}
      onMoveToTrash={handleMoveToTrash}
      expenseCategories={availableExpenseCategories}
      onChange={handleChange}
      trashConfirmation={trashConfirmation}
      onConfirm={moveToTrashCallback}
      onCancel={closeTrashConfirmation}
    />
  );
};

ExpenseListItemContainer.propTypes = {
  id: PropTypes.number.isRequired,
};
